import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must an Irish invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A standard Irish invoice must include: your business name and address; the customer name and address; a unique invoice number; the invoice date; a description of goods or services; and the amount due. If you are VAT-registered, you must also include your VAT registration number, the VAT rate and amount charged, and the gross total including VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in Ireland?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Ireland has several VAT rates. The standard rate is 23%, which applies to most goods and services. The reduced rate of 13.5% applies to services like construction, hotel accommodation, and restaurant services. A 9% rate applies to certain printed publications and tourism services. Zero rate (0%) applies to most food, children\'s clothing, and exports.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do I need to register for VAT in Ireland?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'VAT registration is compulsory in Ireland once your turnover of taxable services exceeds EUR 37,500 per year (EUR 75,000 for goods). Below this threshold, VAT registration is optional. Once registered, you must charge VAT on all taxable supplies and file regular VAT returns with Revenue.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do sole traders and freelancers invoice in Ireland?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Irish sole traders and freelancers issue invoices like any business. If not VAT-registered, simply include your name, address, a unique invoice number, date, service description, and amount. If VAT-registered, add your VAT number and tax breakdown. Invilic supports both VAT and standard invoice formats for Irish businesses.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a free invoice for Irish businesses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for Irish businesses, freelancers, and sole traders. Create VAT invoices with 23% VAT auto-calculated, or standard invoices in EUR. Download a professional PDF instantly — free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered Irish businesses',
    features: ['23% VAT auto-calculated', 'VAT registration number field', 'Net + VAT + gross total', 'Revenue-compliant format', 'EUR currency'],
    to: '/create/vat',
    color: 'emerald',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below EUR 37,500',
    features: ['No VAT required', 'EUR currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP billing', 'SWIFT / IBAN fields', 'EU reverse charge support', 'Multi-currency'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorIrelandPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Ireland: Free VAT Invoice Maker | Invilic"
        description="Free invoice generator for Irish businesses, freelancers, and sole traders. Create VAT invoices at 23% or standard invoices in EUR. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-ireland"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇮🇪 Ireland
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Ireland
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Irish businesses, freelancers, and sole traders. Create Revenue-compliant VAT invoices with 23% VAT auto-calculated, or standard invoices in EUR. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200">
              Create VAT Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">VAT Invoice vs Standard Invoice in Ireland</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-3">Use a VAT Invoice if:</div>
              {[
                'Your annual service turnover exceeds EUR 37,500 (EUR 75,000 for goods)',
                'You are registered for VAT with Revenue',
                'Your client is VAT-registered and needs to reclaim VAT',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your turnover is below the EUR 37,500 VAT threshold',
                'You are a sole trader or freelancer not VAT-registered',
                'Your client does not need a VAT invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Irish Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Irish Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Irish businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
